import React, { useReducer, useEffect, useRef, forwardRef, useState, useMemo } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from "@/components/ui/table/Table";
import type { Document } from "@/services/api/documents/types";
import { format } from "date-fns";
import { ErrorBanner } from "@/components/ErrorBanner";
import {
  getFileTypeIcon,
  getReadableFileType,
  formatFileSize,
} from "@/utils/files";
import { useWebSocket } from "@/context/WebSocketContext";
import { AgenticAPI } from "@/services/api";
import FormInput from "@/components/ui/form-input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faFileLines } from "@awesome.me/kit-70c1f0055f/icons/classic/light";

// Define the state type
type State = {
  documents: Document[];
  loading: boolean;
  error: string | null;
  refreshCounter: number;
};

// Define the action types
type Action =
  | { type: "FETCH_START" }
  | { type: "FETCH_SUCCESS"; payload: Document[] }
  | { type: "FETCH_ERROR"; payload: string }
  | { type: "REFRESH" }
  | { type: "ADD_DOCUMENT"; payload: Document };

// Define the reducer function
function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "FETCH_START":
      return { ...state, loading: true, error: null };
    case "FETCH_SUCCESS":
      return { ...state, loading: false, documents: action.payload };
    case "FETCH_ERROR":
      return { ...state, loading: false, error: action.payload };
    case "REFRESH":
      return {
        ...state,
        loading: true,
        refreshCounter: state.refreshCounter + 1,
      };
    case "ADD_DOCUMENT":
      if (
        state.documents.some(
          (doc) => doc.document_id === action.payload.document_id
        )
      ) {
        return state;
      }
      return {
        ...state,
        documents: [action.payload, ...state.documents],
      };
    default:
      return state;
  }
}

interface FilesListProps {
  entityId?: string;
  entityType?: "worker" | "team" | "department" | string;
  fetchDocuments: (entityId?: string) => Promise<Document[]>;
  websocketMessageType?: string;
  title?: string;
}

// Define the ref type
export interface FilesListRef {
  refreshDocuments: () => void;
}

export const FilesList = forwardRef<FilesListRef, FilesListProps>(
  function FilesList(
    { entityId, entityType, fetchDocuments, title, websocketMessageType },
    ref
  ) {
    const { lastMessage, subscribe, unsubscribe } = useWebSocket();
    const [searchQuery, setSearchQuery] = useState("");
    
    // Initial state
    const initialState: State = {
      documents: [],
      loading: true,
      error: null,
      refreshCounter: 0,
    };

    // Use reducer instead of multiple useState calls
    const [state, dispatch] = useReducer(reducer, initialState);
    
    // Filter documents by search query
    const filteredDocuments = useMemo(() => {
      if (!searchQuery.trim()) return state.documents;
      const query = searchQuery.toLowerCase();
      return state.documents.filter((doc) => {
        const name = (doc.name || doc.display_name || doc.original_filename || "").toLowerCase();
        const type = getReadableFileType(doc.mime_type || doc.file_type || "").toLowerCase();
        const uploader = (doc.uploaded_by?.name || "").toLowerCase();
        return name.includes(query) || type.includes(query) || uploader.includes(query);
      });
    }, [state.documents, searchQuery]);

    // Refs to track component lifecycle and prevent duplicate requests
    const isMounted = useRef(true);
    const isLoadingRef = useRef(false);

    // Function to load documents
    async function loadDocuments() {
      if (!fetchDocuments || isLoadingRef.current || !isMounted.current) return;

      isLoadingRef.current = true;

      try {
        const data = await fetchDocuments(entityId);

        if (isMounted.current) {
          dispatch({ type: "FETCH_SUCCESS", payload: data });
        }
      } catch (err) {
        console.error("Error loading documents:", err);
        if (isMounted.current) {
          const errorMessage =
            err instanceof Error ? err.message : "Failed to load documents";
          dispatch({ type: "FETCH_ERROR", payload: errorMessage });
        }
      } finally {
        isLoadingRef.current = false;
      }
    }

    // Function to manually trigger a refresh
    function refreshDocuments() {
      if (!isLoadingRef.current && isMounted.current) {
        dispatch({ type: "REFRESH" });
      }
    }

    // Expose the refresh function via ref
    React.useImperativeHandle(
      ref,
      () => ({
        refreshDocuments,
      }),
      []
    );

    // Cleanup on unmount
    useEffect(() => {
      isMounted.current = true;
      return () => {
        isMounted.current = false;
      };
    }, []);

    // Effect to handle initial load and refreshes
    useEffect(() => {
      loadDocuments();
    }, [entityId, fetchDocuments, state.refreshCounter]);

    // Effect to subscribe and unsubscribe from WebSocket channel
    useEffect(() => {
      if (websocketMessageType) {
        console.log(
          "[FilesList] Subscribing to WebSocket channel:",
          websocketMessageType
        );
        subscribe(websocketMessageType);
        return () => {
          console.log(
            "[FilesList] Unsubscribing from WebSocket channel:",
            websocketMessageType
          );
          unsubscribe(websocketMessageType);
        };
      }
    }, [websocketMessageType, subscribe, unsubscribe]);

    // Listen for real-time document updates via WebSocket
    useEffect(() => {
      if (!websocketMessageType) return;

      const message: any = lastMessage.get(websocketMessageType);

      if (
        message &&
        message.operation === "insert" &&
        message.full_document &&
        message.full_document.pod_instance_id === entityId
      ) {
        const fullDoc = message.full_document;

        // Transform full_document to Document type
        // Ensure all fields required by the Document type are present or have defaults
        const newDocument: Document = {
          document_id: fullDoc._id,
          filename:
            fullDoc.name ||
            fullDoc.original_filename ||
            fullDoc.display_name ||
            "Unnamed Document",
          file_type: fullDoc.mime_type || "unknown", // Fallback for file_type
          upload_date:
            fullDoc.uploaded_at ||
            fullDoc.created_at ||
            new Date().toISOString(),
          user_id: fullDoc.uploaded_by?.id || "unknown", // Fallback for user_id
          resource_type: entityType || "instance", // Use entityType from props or default
          resource_id: entityId || null, // Use entityId from props
          metadata: fullDoc.metadata || {},
          chunk_count: fullDoc.chunk_count || 0, // Default chunk_count
          as_at:
            fullDoc.updated_at ||
            fullDoc.created_at ||
            new Date().toISOString(), // Default as_at
          landscape:
            typeof fullDoc.landscape === "boolean" ? fullDoc.landscape : false, // Default landscape
          // Optional fields from Document type
          display_name: fullDoc.display_name,
          original_filename: fullDoc.original_filename,
          size: fullDoc.size,
          mime_type: fullDoc.mime_type,
          uploaded_by: fullDoc.uploaded_by
            ? { id: fullDoc.uploaded_by.id, name: fullDoc.uploaded_by.name }
            : undefined,
        };

        if (isMounted.current) {
          console.log(
            "[FilesList] Adding new document via WebSocket:",
            newDocument
          );
          dispatch({ type: "ADD_DOCUMENT", payload: newDocument });
        }
      }
    }, [lastMessage, entityId, entityType, websocketMessageType, dispatch]);

    // Add download handler
    const handleDownload = async (doc: Document) => {
      if (!doc.document_id) {
        console.error("Unable to download: Document ID not found");
        alert("Unable to download: Document ID not found");
        return;
      }

      // Check if document has URL properties (these come from agentic API mapping)
      const docWithUrls = doc as Document & { url?: string; render_url?: string };
      const hasDownloadableFile = docWithUrls.url && !docWithUrls.url.includes('render');
      const hasRenderUrl = docWithUrls.render_url;

      // If no downloadable file but has render URL, open in new tab
      if (!hasDownloadableFile && hasRenderUrl) {
        console.log(`Opening render URL for ${doc.display_name || doc.original_filename}: ${hasRenderUrl}`);
        window.open(hasRenderUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      // If no downloadable file and no render URL, show error
      if (!hasDownloadableFile && !hasRenderUrl) {
        alert("This document is not available for download or viewing");
        return;
      }

      // Otherwise, proceed with download
      try {
        // Use the AgenticAPI download method
        const blob = await AgenticAPI.downloadAgenticDocument(doc.document_id);

        // Determine filename
        const filename = doc.name ||
                        doc.display_name ||
                        doc.original_filename ||
                        'download';

        // Create download link and trigger download
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body?.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body?.removeChild(a);

        console.log(`Downloaded ${filename} successfully`);
      } catch (error) {
        console.error('Download error:', error);

        // If download fails, try render_url as fallback
        if (hasRenderUrl) {
          console.log(`Download failed, falling back to render URL: ${hasRenderUrl}`);
          window.open(hasRenderUrl, '_blank', 'noopener,noreferrer');
        } else {
          alert(`Failed to download file: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }
    };

    // Render based on state
    if (state.loading) {
      return (
        <div className="h-full flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-slate-200 dark:border-slate-700 border-t-indigo-500 rounded-full animate-spin" />
            <p className="text-sm text-slate-500 dark:text-slate-400">Loading documents...</p>
          </div>
        </div>
      );
    }

    if (state.error) {
      return (
        <div className="p-4 flex h-full w-full">
          <ErrorBanner message={`Error: ${state.error}`} />
        </div>
      );
    }

    if (state.documents.length === 0) {
      return (
        <div className="h-full flex items-center justify-center p-6">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center">
              <FontAwesomeIcon icon={faFileLines} className="text-slate-300 dark:text-slate-600 text-2xl" />
            </div>
            <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">No documents yet</p>
            <p className="text-xs text-slate-400 dark:text-slate-500">Uploaded files will appear here</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full w-full">
        {/* Header with search */}
        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700/50 flex items-center gap-4">
          <div className="flex items-center gap-3 flex-1">
            {title && (
              <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                {title}
              </h3>
            )}
            <span className="text-xs text-slate-400 dark:text-slate-500 tabular-nums">
              {state.documents.length} {state.documents.length === 1 ? 'file' : 'files'}
            </span>
          </div>
          {state.documents.length > 5 && (
            <div className="flex-1 max-w-md">
              <FormInput
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                icon={<FontAwesomeIcon icon={faSearch} />}
                iconPosition="start"
                canClear={true}
                showClearButton={true}
                onClear={() => setSearchQuery("")}
              />
            </div>
          )}
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {filteredDocuments.length === 0 && searchQuery ? (
            <div className="p-6 text-center">
              <p className="text-sm text-slate-400 dark:text-slate-500">
                No files match "{searchQuery}"
              </p>
            </div>
          ) : (
            <Table striped dense>
              <TableHead>
                <TableRow>
                  <TableHeader></TableHeader>
                  <TableHeader>Type</TableHeader>
                  <TableHeader>Size</TableHeader>
                  <TableHeader>Uploaded By</TableHeader>
                  <TableHeader>Uploaded</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDocuments.map((document) => (
                  <TableRow key={document.document_id}>
                    <TableCell>
                      <div className="flex items-center">
                        <div className="flex-shrink-0 w-12 h-full flex items-center justify-center my-auto">
                          {getFileTypeIcon(
                            document.mime_type || document.file_type || ""
                          )}
                        </div>
                        <div className="">
                          <button
                            onClick={() => handleDownload(document)}
                            className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline cursor-pointer text-left"
                            title="Click to download"
                          >
                            {document.name ||
                              document.display_name ||
                              document.original_filename ||
                              "Unnamed Document"}
                          </button>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-500 dark:text-gray-400">
                      {getReadableFileType(
                        document.mime_type || document.file_type || ""
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500 dark:text-gray-400">
                      {formatFileSize(document.size || 0)}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500 dark:text-gray-400">
                      {document.uploaded_by?.name || "-"}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500 dark:text-gray-400">
                      {format(
                        new Date(document.upload_date || new Date()),
                        "MMM d, yyyy"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    );
  }
);
